import { useSyncExternalStore } from "react";
import { Pressable, Text } from "react-native";

import { Button } from "@/components/ui/Button";
import { Sheet } from "@/components/ui/Sheet";
import { answerDialog, currentDialog, subscribeToDialogs } from "@/lib/ui/dialog";

/**
 * Renders the web build's confirms and notices as an in-app dialog. Mounted once, at the app root.
 *
 * Nothing renders while no prompt is pending, and that is required. `react-native-web` stacks
 * modals by DOM order alone (its `ModalPortal` appends one div per mounted `Modal` and sets no
 * z-index), so a host holding its `Modal` open would paint behind any sheet opened after it, and
 * several confirms are raised from inside an open sheet, such as deleting a Location. Mounting on
 * demand appends this panel last, so it paints above and is hit-testable from its first frame. The
 * `key` below keeps that true across a queue hand-off, where reconciling would leave the next
 * prompt in the div already appended for this one.
 *
 * react-native-web registers the active modal from `onShow`, which the fade fires on
 * `animationEnd`, so Escape and the focus trap stay with the modal underneath for about 250ms,
 * including at each hand-off. That is accepted over a prompt that could stay hidden.
 *
 * Native never reaches this: `askConfirm` raises the OS alert there.
 */
export function DialogHost() {
  const dialog = useSyncExternalStore(subscribeToDialogs, currentDialog, currentDialog);
  if (!dialog) return null;
  const { id, title, message, confirm } = dialog;
  const dismiss = () => answerDialog(id, false);
  return (
    // A crossfade rather than a slide, and no grab pill, because a confirm interrupts rather than
    // arrives.
    <Sheet
      key={id}
      onClose={dismiss}
      animation="fade"
      scrim="strong"
      grabber={false}
      label={title}
      backdropLabel={confirm ? confirm.cancelLabel : "Dismiss"}
      className="gap-3 pt-5"
      title={title}
      subtitle={message}
    >
      <Button
        title={confirm?.label ?? "OK"}
        variant={confirm?.destructive ? "destructive" : "default"}
        onPress={() => answerDialog(id, true)}
      />
      {confirm
        ? (
          <Pressable
            onPress={dismiss}
            accessibilityRole="button"
            className="items-center py-1"
          >
            <Text className="font-label text-sm text-muted-foreground">{confirm.cancelLabel}</Text>
          </Pressable>
        )
        : null}
    </Sheet>
  );
}
